import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { Container } from 'reactstrap';
import { isLoggedIn } from '../../services';
import {
    getLastConnectedUser,
    storeUserSettings,
    logOut,
    loadSetting,
    updateUi,
    loadHasOwnMovies,
} from '../../redux';
import { setAvailableEditions } from '../../redux/modules/editionNavigation';
import { Editions } from '../../services';
import { loadTktInstances } from '../../redux/modules/tktInstances';
import { SPOOF_ORIGINAL_USER_STORAGE_KEY } from '../../services/spoofSession';
import { isLoginEntryPathname } from '../../routing/contextUrl';
import MainContent from './MainContent/MainContent';
import ContextGate from '../../components/ContextGate/ContextGate';
import Login from '../../containers/Login/Login';
import Public from '../../containers/Public/Public';
import {Ability, SwUpdateModal, SynchronisationPanel, ChangeLog, Loader, HealthChecks} from '../../components';
import CommandPalette from '../../components/CommandPalette/CommandPalette';
import OfflineBanner from '../../components/OfflineBanner/OfflineBanner';
import Nav from './Nav/Nav';
import './App.css';
import './KronosDataTable.scss';
import i18n from "i18next";
import Utils from "../../services/Utils";
import {
    init_posthog,
    instance_prefix_from_settings,
    set_posthog_context,
    identify_posthog_user,
} from "../../services/posthogAnalytics";
import overrideFR from "../../locales/override-fr.yml";
import overrideEN from "../../locales/override-en.yml";
import overrideDE from "../../locales/override-de.yml";
import overrideIT from "../../locales/override-it.yml";
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';

const App = () => {
    const { pathname }                       = useLocation();
    const [loadingLang, setLoadingLang]      = useState(false);
    const [hasShownApp, setHasShownApp]      = useState(false);
    const [showLangOverlay, setShowLangOverlay]   = useState(false);
    const { user }                           = useSelector(state => state.auth);
    const { KUserSettings }                  = useSelector(state => state.userSettings);
    const { setting, hasLoadingError }       = useSelector(state => state.settings);
    const dispatch                           = useDispatch();
    const { t }                              = useTranslation();

    const loadSpecificConfig = (customerType) => {
        if (customerType in overrideFR)
            i18n.addResourceBundle('fr', 'translations', Utils.dotify(overrideFR[customerType]), true, true);
        if (customerType in overrideEN)
            i18n.addResourceBundle('en', 'translations', Utils.dotify(overrideEN[customerType]), true, true);
        if (customerType in overrideDE)
            i18n.addResourceBundle('de', 'translations', Utils.dotify(overrideDE[customerType]), true, true);
        if (customerType in overrideIT)
            i18n.addResourceBundle('it', 'translations', Utils.dotify(overrideIT[customerType]), true, true);

        setLoadingLang(false);
    }

    useEffect(() => {
        dispatch(getLastConnectedUser());
        // embedded in an iframe
        if (window.self !== window.top) {
            dispatch(updateUi({ isEmbedded: true }));
            document.body.classList.add('embedded');
        }
    }, [dispatch]);

    // Instance list for the sidebar switcher. Superadmins need the full catalog
    // even when SideBar is unmounted during settings bootstrap. Multi-membership
    // users (e.g. volunteers on EIP + GIFF) need their memberships loaded too —
    // otherwise InstanceSelector has empty options and only shows the active org.
    const membership_count = Array.isArray(user?.tkt_instance_ids)
        ? user.tkt_instance_ids.length
        : 0;
    useEffect(() => {
        if (!user?.id) {
            return;
        }
        if (!user?.abilities?.superadmin && membership_count <= 1) {
            return;
        }
        dispatch(loadTktInstances());
    }, [dispatch, user?.id, user?.abilities?.superadmin, membership_count]);

    useEffect(() => {
        dispatch(storeUserSettings(KUserSettings));
    }, [KUserSettings, dispatch])

    useEffect(() => {
        if (user?.id) {
            setLoadingLang(true);
            dispatch(loadSetting('default'));
        }
    }, [dispatch, user?.id]);

    const customer_type = setting?.customer?.type;

    useEffect(() => {
        if (!user?.id || customer_type == null) {
            return;
        }
        dispatch(loadHasOwnMovies(user, { customer: { type: customer_type } }));
    }, [dispatch, user, customer_type]);

    useEffect(() => {
        if (!user?.id) {
            return undefined;
        }
        const can_read_editions = Boolean(
            user?.abilities?.superadmin
            || (typeof user?.can === 'function' && user.can('read', 'editions'))
        );
        if (!can_read_editions) {
            dispatch(setAvailableEditions([]));
            return undefined;
        }
        let cancelled = false;
        Editions.list({})
            .then(({ editions }) => {
                if (!cancelled) {
                    dispatch(setAvailableEditions(editions || []));
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [dispatch, user]);

    useEffect(() => {
        init_posthog();
    }, []);

    useEffect(() => {
        if (!setting)
            return;

        const customer_name = setting.customer?.name_short?.fr
            || setting.customer?.name_short?.en
            || setting.customer?.name
            || undefined;
        const instance = instance_prefix_from_settings(setting);
        set_posthog_context({
            customer_type: setting.customer?.type,
            customer: customer_name,
            instance,
        });
        if (user?.id)
            identify_posthog_user(user.id);
    }, [setting, user?.id]);

    useEffect(() => {
        if (setting && setting.customer) {
            const customerType = setting.customer.type;
            loadSpecificConfig(customerType)
        }
    }, [setting]);

    const theme = KUserSettings.darkTheme ? "dark" : "light";

    useEffect(() => {
        if (hasLoadingError && loadingLang) {
            setLoadingLang(false);
        }
    }, [hasLoadingError, loadingLang]);

    // Once the UI has been displayed at least once, avoid unmounting it again
    // (it causes a visible "page -> loader -> page" flicker on refresh).
    useEffect(() => {
        if (!hasShownApp && isLoggedIn()) {
            setHasShownApp(true);
        }
    }, [hasShownApp]);

    // Avoid flashing a spinner for very fast settings/lang refreshes.
    useEffect(() => {
        if (!loadingLang) {
            setShowLangOverlay(false);
            return;
        }
        const t = window.setTimeout(() => setShowLangOverlay(true), 200);
        return () => window.clearTimeout(t);
    }, [loadingLang]);

    // Password reset / forgot / confirm-email links must work even if a session
    // is still stored (otherwise MainContent shows a misleading Forbidden).
    useEffect(() => {
        if (!isLoginEntryPathname(pathname) || !isLoggedIn()) {
            return;
        }
        // Drop spoof first so logOut() clears the session instead of restoring
        // the original user (who would still block the login entry screen).
        localStorage.removeItem(SPOOF_ORIGINAL_USER_STORAGE_KEY);
        dispatch(logOut());
    }, [dispatch, pathname, user?.id]);

    if (pathname.match('^/(public|p)(/.+)?$')) {
        return (
            <>
                <OfflineBanner />
                <Public />
            </>
        );
    }

    if (isLoginEntryPathname(pathname)) {
        return (
            <>
                <OfflineBanner />
                <Login />
            </>
        );
    }

    if (isLoggedIn()) {
        const showBlockingLoader = loadingLang && !hasShownApp;
        return (
            <div className={`App ${theme}-app`}>
                <OfflineBanner />
                <Container fluid>
                    {showBlockingLoader ? (
                        <div className="container-fluid loader-container justify-content-center">
                            <Loader />
                        </div>
                    ) : (
                        <ContextGate>
                            <Nav />
                            <CommandPalette />
                            {hasLoadingError &&  (
                                <div className="alert alert-danger alert-inline mb-3" role="alert">
                                    <span className="alert-inline-body">
                                        {t("app.errors_load_params")}
                                    </span>
                                </div>
                            )}
                            <MainContent />
                            {showLangOverlay && (
                                <div className="lang-loading-overlay" aria-hidden="true">
                                    <Loader />
                                </div>
                            )}
                        </ContextGate>
                    )}
                </Container>
                <SwUpdateModal />
                <SynchronisationPanel />
                <ChangeLog />
                <Ability can="logs:readOther">
                    <HealthChecks />
                </Ability>
            </div>
        );
    }

    return (
        <>
            <OfflineBanner />
            <Login />
        </>
    );
}

export default App;
